<template>
  <div class="puzzle-game" :class="{ 'game-over': gameOver }">
    <!-- 游戏区域 -->
    <div class="game-container" v-if="gameStarted">
      <div class="game-header">
        <div class="score-board">
          <div class="moves">完成次数: {{ completedMoves }} / 10</div>
          <div class="timer">时间: {{ formatTime(elapsedTime) }}</div>
        </div>
        <div class="target-info">
          <div class="target-label">目标位置</div>
          <div class="target-indicator" :style="{
            top: `${targetPosition.top}px`,
            left: `${targetPosition.left}px`,
            width: `${pieceSize}px`,
            height: `${pieceSize}px`
          }"></div>
        </div>
      </div>

      <!-- 游戏主界面 -->
      <div class="game-main">
        <div
          class="game-board"
          :style="{ width: `${boardSize}px`, height: `${boardSize}px` }"
          @dragover.prevent
          @drop="handleDrop"
        >
          <div class="target-zone" :style="{
            top: `${targetPosition.top}px`,
            left: `${targetPosition.left}px`,
            width: `${pieceSize}px`,
            height: `${pieceSize}px`
          }"></div>
          
          <div
            class="draggable-image"
            :style="{
              top: `${imagePosition.top}px`,
              left: `${imagePosition.left}px`,
              width: `${pieceSize}px`,
              height: `${pieceSize}px`,
              backgroundImage: `url(${puzzleImage})`,
              backgroundSize: 'cover'
            }"
            draggable="true"
            @dragstart="handleDragStart"
          ></div>
        </div>
      </div>

      <!-- 游戏控制 -->
      <div class="game-controls">
        <div class="control-buttons-container">
          <button @click="pauseGame" class="control-btn">
            {{ isPaused ? "继续" : "暂停" }}
          </button>
          <button @click="resetGame" class="control-btn">
            重置
          </button>
          <button @click="backToStartScreen" class="control-btn">
            退出游戏
          </button>
        </div>
      </div>
    </div>

    <!-- 开始界面 -->
    <div class="start-screen" v-if="!gameStarted">
      <div class="start-content">
        <h1>图片拖放挑战🎯</h1>
        <p class="game-description">
          拖动图片到随机生成的目标位置！挑战最短的时间完成更多目标。
        </p>

        <!-- 难度选择 UI -->
        <div class="difficulty-selection">
          <h3>选择难度</h3>
          <div class="difficulty-buttons">
            <button
              v-for="(level, index) in difficultyLevels"
              :key="index"
              @click="selectedDifficulty = index"
              class="difficulty-btn"
              :class="{ active: selectedDifficulty === index }"
            >
              {{ level }}
              <div class="difficulty-icon">{{ difficultyIcons[index] }}</div>
            </button>
          </div>
          <div class="difficulty-description">
            {{ difficultyDescriptions[selectedDifficulty] }}
          </div>
        </div>

        <!-- 拼图选择 -->
        <div class="puzzle-selection">
          <h3>选择图片</h3>
          <div class="puzzle-options">
            <div
              v-for="(image, index) in puzzleOptions"
              :key="index"
              class="puzzle-option"
              :class="{ active: selectedPuzzle === index }"
              @click="selectedPuzzle = index"
            >
              <img :src="image" alt="图片选项" />
            </div>
          </div>
        </div>

        <button @click="startGame" class="start-btn">开始游戏</button>
        <button @click="backToMap" class="back-btn">返回</button>
      </div>
    </div>

    <!-- 游戏结束界面 -->
    <div class="game-over-screen" v-if="gameOver">
      <div class="game-over-content">
        <h2>恭喜完成挑战！🎉</h2>

        <div class="puzzle-report">
          <h3>游戏报告</h3>

          <div class="report-grid">
            <div class="report-item">
              <div class="report-icon">🎯</div>
              <div class="report-label">难度</div>
              <div class="report-value">
                {{ difficultyLevels[selectedDifficulty] }}
              </div>
            </div>

            <div class="report-item">
              <div class="report-icon">🚶</div>
              <div class="report-label">完成次数</div>
              <div class="report-value">{{ completedMoves }}</div>
            </div>

            <div class="report-item">
              <div class="report-icon">⏱️</div>
              <div class="report-label">完成时间</div>
              <div class="report-value">{{ formatTime(elapsedTime) }}</div>
            </div>

            <div class="report-item">
              <div class="report-icon">🏆</div>
              <div class="report-label">最佳记录</div>
              <div class="report-value">{{ getBestRecord() }}</div>
            </div>
          </div>
        </div>

        <div class="completed-puzzle">
          <img :src="puzzleImage" alt="完成的图片" />
        </div>

        <div class="game-over-buttons">
          <button @click="restartGame" class="restart-btn">再玩一次</button>
          <button @click="backToStartScreen" class="back-btn">返回</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "DragImageGame",
  data() {
    return {
      gameStarted: false,
      gameOver: false,
      isPaused: false,
      completedMoves: 0,
      targetCount: 10, // 目标数量，完成这么多个目标后游戏结束
      currentTargetIndex: 0,
      startTime: null,
      elapsedTime: 0,
      timerInterval: null,
      pauseStartTime: null,
      totalPausedTime: 0,
      boardSize: 300,
      pieceSize: 60,
      levelId: "",
      difficultyLevels: ["简单", "中等", "困难"],
      difficultyIcons: ["🌱", "🌿", "🔥"],
      difficultyDescriptions: [
        "大尺寸图像，较大目标区域，容易拖放",
        "中等尺寸图像，标准目标区域",
        "小尺寸图像，较小目标区域，拖放有挑战性",
      ],
      selectedDifficulty: 0,
      puzzleOptions: [
        "https://wugecdn.steam.fun/typefun/images/puzzle/joker.png",
        "https://wugecdn.steam.fun/typefun/images/puzzle/chicken.png",
        "https://wugecdn.steam.fun/typefun/images/puzzle/crab.png",
        "https://wugecdn.steam.fun/typefun/images/puzzle/butterfly.png",
      ],
      selectedPuzzle: 0,
      bestRecords: {},
      targetPosition: {
        top: 0,
        left: 0
      },
      imagePosition: {
        top: 0,
        left: 0
      },
      targetHitThreshold: 20, // 距离目标多近算命中
    };
  },
  computed: {
    puzzleImage() {
      return this.puzzleOptions[this.selectedPuzzle];
    },
  },
  mounted() {
    this.getRouteParams();
    this.loadBestRecords();
  },
  beforeDestroy() {
    this.stopTimer();
  },
  methods: {
    getRouteParams() {
      const { levelId } = this.$route.query;
      this.levelId = levelId || "intro-mouse";
    },

    loadBestRecords() {
      const storedRecords = localStorage.getItem(
        `dragImageRecords_${this.levelId}`
      );
      if (storedRecords) {
        this.bestRecords = JSON.parse(storedRecords);
      }
    },

    saveBestRecord() {
      const difficultyKey = `${this.selectedDifficulty}_${this.selectedPuzzle}`;
      const currentRecord = this.bestRecords[difficultyKey];

      if (
        !currentRecord ||
        this.completedMoves > currentRecord.moves ||
        (this.completedMoves === currentRecord.moves &&
          this.elapsedTime < currentRecord.time)
      ) {
        this.bestRecords[difficultyKey] = {
          moves: this.completedMoves,
          time: this.elapsedTime,
        };

        localStorage.setItem(
          `dragImageRecords_${this.levelId}`,
          JSON.stringify(this.bestRecords)
        );
      }
    },

    getBestRecord() {
      const difficultyKey = `${this.selectedDifficulty}_${this.selectedPuzzle}`;
      const record = this.bestRecords[difficultyKey];

      if (record) {
        return `${record.moves}次 / ${this.formatTime(record.time)}`;
      }

      return "无记录";
    },

    startGame() {
      this.gameStarted = true;
      this.gameOver = false;
      this.isPaused = false;
      this.completedMoves = 0;
      this.currentTargetIndex = 0;
      this.elapsedTime = 0;
      this.totalPausedTime = 0;
      this.pauseStartTime = null;
      
      // 根据难度调整大小
      const sizeFactor = [1, 0.8, 0.6][this.selectedDifficulty];
      this.pieceSize = Math.floor(80 * sizeFactor);
      
      // 设置初始位置
      this.resetImagePosition();
      this.generateNewTarget();
      
      this.startTimer();
    },
    
    resetImagePosition() {
      // 将图片放在左上角
      this.imagePosition = {
        top: 20,
        left: 20
      };
    },
    
    generateNewTarget() {
      // 生成随机目标位置，确保在边界内
      const maxX = this.boardSize - this.pieceSize - 20;
      const maxY = this.boardSize - this.pieceSize - 20;
      
      // 随机生成，但避免与当前图片位置太近
      let newX, newY;
      do {
        newX = Math.floor(Math.random() * maxX) + 20;
        newY = Math.floor(Math.random() * maxY) + 20;
      } while (
        Math.abs(newX - this.imagePosition.left) < this.pieceSize &&
        Math.abs(newY - this.imagePosition.top) < this.pieceSize
      );
      
      this.targetPosition = {
        top: newY,
        left: newX
      };
    },
    
    handleDragStart(event) {
      if (this.isPaused || this.gameOver) return;
      // 设置拖动数据
      event.dataTransfer.setData("text/plain", "draggable-image");
      event.dataTransfer.effectAllowed = "move";
    },
    
    handleDrop(event) {
      if (this.isPaused || this.gameOver) return;
      
      event.preventDefault();
      
      // 获取鼠标在目标元素内的相对位置
      const boardRect = event.currentTarget.getBoundingClientRect();
      const x = event.clientX - boardRect.left - (this.pieceSize / 2);
      const y = event.clientY - boardRect.top - (this.pieceSize / 2);
      
      // 确保图片在游戏板内
      const newX = Math.max(0, Math.min(x, this.boardSize - this.pieceSize));
      const newY = Math.max(0, Math.min(y, this.boardSize - this.pieceSize));
      
      // 更新图片位置
      this.imagePosition = {
        top: newY,
        left: newX
      };
      
      // 检查是否命中目标
      this.checkTargetHit();
    },
    
    checkTargetHit() {
      // 计算图片中心点与目标中心点的距离
      const imageCenterX = this.imagePosition.left + (this.pieceSize / 2);
      const imageCenterY = this.imagePosition.top + (this.pieceSize / 2);
      
      const targetCenterX = this.targetPosition.left + (this.pieceSize / 2);
      const targetCenterY = this.targetPosition.top + (this.pieceSize / 2);
      
      const distance = Math.sqrt(
        Math.pow(imageCenterX - targetCenterX, 2) + 
        Math.pow(imageCenterY - targetCenterY, 2)
      );
      
      // 如果距离在阈值内，认为命中目标
      if (distance <= this.targetHitThreshold) {
        this.playSuccessSound();
        this.completedMoves++;
        this.currentTargetIndex++;
        
        // 检查是否达到目标数量
        if (this.currentTargetIndex >= this.targetCount) {
          this.gameOver = true;
          this.stopTimer();
          this.saveBestRecord();
          this.playWinSound();
          
          // 记录完成关卡
          const completedLevels = JSON.parse(
            localStorage.getItem("completedLevels") || "[]"
          );
          if (!completedLevels.includes(this.levelId)) {
            completedLevels.push(this.levelId);
            localStorage.setItem(
              "completedLevels",
              JSON.stringify(completedLevels)
            );
          }
        } else {
          // 生成新的目标位置
          this.generateNewTarget();
        }
      }
    },

    startTimer() {
      this.startTime = Date.now();
      this.timerInterval = setInterval(() => {
        if (!this.isPaused) {
          this.elapsedTime = Math.floor(
            (Date.now() - this.startTime - this.totalPausedTime) / 1000
          );
        }
      }, 1000);
    },

    stopTimer() {
      clearInterval(this.timerInterval);
    },

    pauseGame() {
      this.isPaused = !this.isPaused;

      if (this.isPaused) {
        this.pauseStartTime = Date.now();
      } else {
        this.totalPausedTime += Date.now() - this.pauseStartTime;
        this.pauseStartTime = null;
      }
    },

    resetGame() {
      this.stopTimer();
      this.startGame();
    },

    restartGame() {
      this.startGame();
    },

    backToMap() {
      this.$router.go(-1);
    },

    backToStartScreen() {
      this.gameOver = false;
      this.gameStarted = false;
      this.stopTimer();
    },

    formatTime(seconds) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${String(minutes).padStart(2, "0")}:${String(
        remainingSeconds
      ).padStart(2, "0")}`;
    },

    playSuccessSound() {
      const audio = new Audio(
        "https://wugecdn.steam.fun/typefun/music/click.mp3"
      );
      audio.volume = 0.3;
      audio.play();
    },

    playWinSound() {
      const audio = new Audio(
        "https://wugecdn.steam.fun/typefun/music/win.mp3"
      );
      audio.play();
    },
  },
};
</script>

<style scoped>
.puzzle-game {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  width: 100%;
  height: 100vh;
      background: linear-gradient(135deg, #1a2a6c, #3a8ee6, #fdbb2d);
  color: white;
  font-family: "Arial", sans-serif;
  position: fixed;
  top: 60;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  overflow: hidden;
}

.game-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  max-width: 800px;
  height: 100vh;
  padding: 20px;
  position: relative;
  z-index: 100;
}

.game-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 20px;
}

.score-board {
  display: flex;
  flex-direction: column;
  font-size: 18px;
  background: rgba(0, 0, 0, 0.2);
  padding: 10px 20px;
  border-radius: 10px;
  min-width: 150px;
}

.target-info {
  text-align: center;
  background: rgba(0, 0, 0, 0.2);
  padding: 10px;
  border-radius: 10px;
}

.target-label {
  font-size: 14px;
  margin-bottom: 5px;
}

.target-indicator {
  width: 20px;
  height: 20px;
  background: rgba(255, 255, 0, 0.3);
  border: 2px dashed yellow;
  border-radius: 4px;
  margin: 0 auto;
  transition: all 0.3s ease;
}

.game-main {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin-bottom: 20px;
}

.game-board {
  position: relative;
  width: 300px;
  height: 300px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.target-zone {
  position: absolute;
  border: 2px dashed yellow;
  background: rgba(255, 255, 0, 0.2);
  border-radius: 5px;
  z-index: 1;
  transition: all 0.3s ease;
  pointer-events: none;
}

.draggable-image {
  position: absolute;
  background-size: cover;
  cursor: grab;
  transition: all 0.1s ease;
  z-index: 2;
  border-radius: 5px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
}

.draggable-image:active {
  cursor: grabbing;
  transform: scale(1.05);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.4);
}

.game-controls {
  display: flex;
  justify-content: center;
  width: 100%;
  margin-top: 20px;
}

.control-buttons-container {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
}

.control-btn {
  padding: 10px 20px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50px;
  color: white;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.control-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: translateY(-2px);
}

/* 开始屏幕样式 */
.start-screen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #1a2a6c, #3a8ee6, #f8c46f);
  z-index: 10000;
}

.start-content {
  background: rgba(0, 0, 0, 0.6);
  border-radius: 15px;
  padding: 25px;
  max-width: 450px;
  width: 90%;
  text-align: center;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.start-content h1 {
  margin-bottom: 15px;
  font-size: 28px;
  background: linear-gradient(135deg, #1a2a6c, #ffffff, #f8c46f);
    -webkit-background-clip: text;
    background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  letter-spacing: 1px;
}

.game-description {
  font-size: 14px;
  line-height: 1.4;
  margin-bottom: 20px;
  padding: 8px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
}

.difficulty-selection,
.puzzle-selection {
  margin-bottom: 15px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 12px 10px;
}

.difficulty-selection h3,
.puzzle-selection h3 {
  margin-bottom: 10px;
  font-size: 16px;
  color: #f6d365;
}

.difficulty-buttons {
  display: flex;
  justify-content: center;
  gap: 8px;
}

.difficulty-btn {
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: white;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 70px;
}

.difficulty-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  transform: translateY(-3px);
}

.difficulty-btn.active {
  background: rgba(246, 211, 101, 0.2);
  border-color: rgba(246, 211, 101, 0.5);
  box-shadow: 0 0 15px rgba(246, 211, 101, 0.3);
  transform: translateY(-3px) scale(1.05);
}

.difficulty-icon {
  font-size: 18px;
  margin-top: 4px;
}

.difficulty-description {
  text-align: center;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  margin-top: 8px;
  min-height: 30px;
  padding: 0 5px;
  line-height: 1.2;
}

.puzzle-options {
  display: flex;
  justify-content: center;
  gap: 10px;
  flex-wrap: wrap;
}

.puzzle-option {
  width: 65px;
  height: 65px;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.puzzle-option.active {
  border-color: #f6d365;
  box-shadow: 0 0 15px rgba(246, 211, 101, 0.5);
  transform: scale(1.1);
}

.puzzle-option:hover {
  transform: scale(1.05);
}

.puzzle-option img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.start-btn,
.back-btn {
  padding: 10px 20px;
  font-size: 14px;
  border: none;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  margin: 8px 6px;
  font-weight: 600;
  letter-spacing: 1px;
}

.start-btn {
  background: linear-gradient(135deg, #f6d365, #fda085);
  color: #333;
  box-shadow: 0 5px 15px rgba(246, 211, 101, 0.4);
}

.back-btn {
  background: rgba(255, 255, 255, 0.08);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.start-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(246, 211, 101, 0.6);
}

.back-btn:hover {
  background: rgba(255, 255, 255, 0.15);
  transform: translateY(-3px);
}

/* 游戏结束屏幕样式 */
.game-over-screen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.7);
  z-index: 10001;
  backdrop-filter: blur(5px);
}

.game-over-content {
  background: rgba(0, 0, 0, 0.8);
  border-radius: 20px;
  padding: 25px;
  max-width: 450px;
  width: 85%;
  text-align: center;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  animation: fadeIn 0.5s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.game-over-content h2 {
  margin-bottom: 15px;
  font-size: 26px;
  color: #f6d365;
  text-shadow: 0 2px 5px rgba(0, 0, 0, 0.3);
}

.puzzle-report {
  margin-bottom: 20px;
}

.puzzle-report h3 {
  margin-bottom: 15px;
  font-size: 18px;
  color: white;
}

.report-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

.report-item {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  padding: 12px 8px;
  text-align: center;
  transition: transform 0.2s ease;
}

.report-item:hover {
  transform: translateY(-3px);
  background: rgba(255, 255, 255, 0.08);
}

.report-icon {
  font-size: 24px;
  margin-bottom: 4px;
}

.report-label {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 3px;
}

.report-value {
  font-size: 16px;
  font-weight: bold;
}

.completed-puzzle {
  width: 150px;
  height: 150px;
  margin: 0 auto 20px;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
  transition: transform 0.3s ease;
}

.completed-puzzle:hover {
  transform: scale(1.05);
}

.completed-puzzle img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.game-over-buttons {
  display: flex;
  justify-content: center;
  gap: 12px;
}

.restart-btn,
.back-btn {
  padding: 10px 20px;
  font-size: 14px;
  border: none;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 600;
}

.restart-btn {
  background: linear-gradient(135deg, #f6d365, #fda085);
  color: #333;
}

.restart-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 15px rgba(246, 211, 101, 0.3);
}

.back-btn:hover {
  background: rgba(255, 255, 255, 0.15);
  transform: translateY(-3px);
}

/* 响应式调整 */
@media (max-width: 768px) {
  .start-content {
    padding: 20px;
    width: 85%;
  }

  .start-content h1 {
    font-size: 24px;
  }

  .game-description {
    font-size: 14px;
  }

  .difficulty-buttons {
    flex-wrap: wrap;
  }

  .game-board {
    width: 280px;
    height: 280px;
  }

  .game-over-content {
    padding: 20px;
    max-width: 320px;
  }

  .game-over-content h2 {
    font-size: 22px;
  }

  .completed-puzzle {
    width: 120px;
    height: 120px;
  }

  .report-grid {
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }

  .report-item {
    padding: 8px 5px;
  }

  .report-icon {
    font-size: 20px;
  }

  .report-value {
    font-size: 14px;
  }
}
</style>
